/**
 * Web Push subscribe/unsubscribe helpers for My SPAN.
 * All network calls go through the same axios/withCredentials cookie session.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const notificationPermission = () =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

export async function getExistingPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeUser() {
  if (!pushSupported()) throw new Error('Push notifications are not supported on this device.');

  // Permission
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('You need to allow notifications to enable this.');

  // Ensure SW is ready (in dev the SW may not be registered)
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    try {
      reg = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
    } catch (_) {
      throw new Error('Service worker unavailable — try again in a moment.');
    }
  }
  await navigator.serviceWorker.ready;

  // Fetch the server's VAPID public key
  const { data: keyData } = await axios.get(`${API}/push/vapid-public-key`, { withCredentials: true });
  if (!keyData.key) throw new Error('Server is missing VAPID configuration.');

  // Reuse or create the subscription
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyData.key),
    });
  }

  await axios.post(
    `${API}/push/subscribe`,
    { subscription: sub.toJSON(), user_agent: navigator.userAgent },
    { withCredentials: true }
  );
  return sub;
}

export async function unsubscribeUser() {
  if (!pushSupported()) return;
  const sub = await getExistingPushSubscription();
  if (!sub) return;
  try {
    await axios.post(
      `${API}/push/unsubscribe`,
      { subscription: sub.toJSON() },
      { withCredentials: true }
    );
  } catch (_) {}
  await sub.unsubscribe();
}

export async function sendTestPush() {
  const { data } = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
  return data;
}
